import { computed } from 'vue'
import {
  PEOPLE_AUTOCOMPLETE,
  WINTER_SERVICE_VEHICLES_AUTOCOMPLETE
} from '@/modules/road-maintenance-module/winter-service-v2/common/config/constants'
import { useTaskVehicleFacts } from '@/modules/road-maintenance-module/winter-service-v2/tasks/composables/useTaskVehicleFacts'
import {
  ABNORMAL_FIELD_CLASS,
  REQUESTS_TRANSLATION_NAMESPACE as NS
} from '@/modules/road-maintenance-module/winter-service-v2/requests/config/constants'

const COLUMNS_COUNT = 2

/**
 * The fields of a single winter service request, grouped the way the design shows them.
 *
 * Almost all of it belongs to the task the request was made from, so it is shown and
 * never edited here - the task's own screen is where that is changed. The two exceptions
 * are the vehicle and the driver: those are the request's own decision - the only two
 * fields it lets a base manager pick, and the only two ever sent back (see
 * useRequestBasicInfo) - which is also why they are the two that get framed red when
 * they can no longer take it.
 */
export function useRequestFields (options) {
  const { translate, isVehicleBlocked, isDriverInactive } = options || {}

  // The vehicle is the one thing picked here, and everything the vehicle decides follows
  // it - so the same facts the task screens keep are read here too.
  const { rememberVehicles, pickVehicle } = useTaskVehicleFacts()

  const readOnlyField = (key, label, extra = {}) => ({
    key,
    label,
    creatable: false,
    editable: false,
    visible: true,
    ...extra
  })

  const editableField = (key, label, extra = {}) => ({
    key,
    label,
    creatable: true,
    editable: true,
    visible: true,
    ...extra
  })

  /**
   * The frame the design puts around a value that stands in the way of a work order.
   * It goes on as a class rather than a style: the border belongs to the input's own
   * fieldset, which sits inside the field component - `props` is FieldsRenderer's
   * pass-through to it.
   */
  const abnormalFrame = (isAbnormalValue) => {
    if (!isAbnormalValue) {
      return {}
    }

    return {
      props: {
        class: ABNORMAL_FIELD_CLASS
      }
    }
  }

  const basicDataRow = computed(() => ({
    type: 'fields',
    title: translate(`${NS}.basic_information`),
    columnsCount: COLUMNS_COUNT,
    fields: [
      readOnlyField('status', translate(`${NS}.status`)),
      readOnlyField('id', translate(`${NS}.id`)),
      readOnlyField('task_type', translate(`${NS}.task_type`)),
      readOnlyField('surface_type', translate(`${NS}.surface_type`)),
      readOnlyField('task_category', translate(`${NS}.task_category`))
    ]
  }))

  const cleaningRow = computed(() => ({
    type: 'fields',
    title: translate(`${NS}.cleaning_data`),
    columnsCount: COLUMNS_COUNT,
    fields: [
      readOnlyField('cleaning_type', translate(`${NS}.cleaning_type`))
    ]
  }))

  /**
   * A different registration brings its own vehicle type, drive, throw side, contractor,
   * base, responsible person, subcontractor and operator with it - none of them the
   * request's to decide, all of them shown here. The values the task snapshot filled in
   * belong to the task's vehicle, so picking another one has to replace them.
   *
   * Read-only fields every one of them: the save sends the vehicle and the driver and
   * nothing else (see useRequestBasicInfo).
   */
  const onVehicleChange = ({ selectedItem, updateData }) => {
    updateData?.(pickVehicle(selectedItem?.id ?? selectedItem))
  }

  const vehicleRow = computed(() => ({
    type: 'fields',
    title: translate(`${NS}.vehicle_data`),
    columnsCount: COLUMNS_COUNT,
    fields: [
      readOnlyField('vehicle_type', translate(`${NS}.vehicle_type`)),
      readOnlyField('drive_type', translate(`${NS}.drive_type`)),
      // The request's own: which vehicle takes it. Framed red while the picked one is
      // out of service, since the request cannot become a work order that way.
      editableField('vehicle_id', translate(`${NS}.registration`), {
        type: 'autocomplete',
        // A winter service request may only point at a vehicle set up for it.
        autocomplete_options: {
          ...WINTER_SERVICE_VEHICLES_AUTOCOMPLETE,
          // The field hands back the picked id and nothing else, so the rows are kept
          // as they arrive and looked up again on change.
          onDataReceive: ({ normalizedData }) => rememberVehicles(normalizedData)
        },
        onChange: onVehicleChange,
        ...abnormalFrame(isVehicleBlocked?.value)
      })
    ]
  }))

  const crewRow = computed(() => ({
    type: 'fields',
    title: translate(`${NS}.crew_data`),
    columnsCount: COLUMNS_COUNT,
    fields: [
      // The request's other own decision: who drives it. Framed red while the picked one
      // is no longer active, since the request cannot become a work order that way.
      editableField('driver_id', translate(`${NS}.driver`), {
        type: 'autocomplete',
        autocomplete_options: PEOPLE_AUTOCOMPLETE,
        ...abnormalFrame(isDriverInactive?.value)
      }),
      readOnlyField('worker_count', translate(`${NS}.worker_count`))
    ]
  }))

  const spreadingRow = computed(() => ({
    type: 'fields',
    title: translate(`${NS}.spreading_data`),
    columnsCount: COLUMNS_COUNT,
    fields: [
      readOnlyField('spread_type', translate(`${NS}.spread_type`)),
      readOnlyField('spread_quantity', translate(`${NS}.spread_quantity`)),
      readOnlyField('throw_side', translate(`${NS}.throw_side`))
    ]
  }))

  const contractorRow = computed(() => ({
    type: 'fields',
    title: translate(`${NS}.contractor_data`),
    columnsCount: COLUMNS_COUNT,
    fields: [
      readOnlyField('ownership', translate(`${NS}.contractor`)),
      readOnlyField('base_name', translate(`${NS}.responsible_base`)),
      readOnlyField('operator_name', translate(`${NS}.operator`)),
      readOnlyField('base_responsible_person', translate(`${NS}.base_responsible_person`)),
      readOnlyField('subcontractor_name', translate(`${NS}.subcontractor`))
    ]
  }))

  const rows = computed(() => ([
    basicDataRow.value,
    cleaningRow.value,
    vehicleRow.value,
    crewRow.value,
    spreadingRow.value,
    contractorRow.value
  ]))

  return {
    rows
  }
}
